<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  Button, Description, ErrorMessage, Field, Input, InlineAlert, Label, Radio, RadioField, RadioGroup,
} from 'catalyst-vue'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryStore } from '../../stores/library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import { editComics } from '../../lib/category-service'
import { type BulkOutcome } from '../../lib/bulk-progress'
import { previewEdit, summarise, type EditableField, type EditRule } from '../../lib/title-rename'
import LibraryOverlay from './library-overlay.vue'

/**
 * 批次編輯標題或作者。
 *
 * 套用前逐本列出目前值與結果值，不抽樣不截斷——批次編輯沒有復原機制，預覽是唯一的防線。
 * 作者欄能放進來正是因為有這條防線：原本被列為「明確不做」的理由是盲目覆寫的風險，
 * 而風險來自看不到結果，不是欄位本身。
 */
const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: []; done: [outcome: BulkOutcome, field: EditableField] }>()
const cloud = useCloudLibraryStore()
const local = useLibraryStore()
const store = useLibraryEntriesStore()

const field = ref<EditableField>('title')
const titleRule = ref<'derive' | 'replace'>('derive')
const authorRule = ref<'set' | 'replace'>('set')
const authorValue = ref('')
const find = ref('')
const replacement = ref('')
const busy = ref(false)
const error = ref('')

// 重新推導需要資料夾路徑，只有這台裝置上的作品才有；選取中有別台上傳的作品時不提供。
const canDerive = computed(() => store.allSelectedAreLocal)

watch(() => props.open, isOpen => {
  if (!isOpen) return
  field.value = 'title'
  titleRule.value = canDerive.value ? 'derive' : 'replace'
  authorRule.value = 'set'
  authorValue.value = ''
  find.value = ''
  replacement.value = ''
  error.value = ''
})
watch(canDerive, able => { if (!able && titleRule.value === 'derive') titleRule.value = 'replace' })

const usingReplace = computed(() => field.value === 'title' ? titleRule.value === 'replace' : authorRule.value === 'replace')
const rule = computed<EditRule>(() => {
  if (field.value === 'author') {
    return authorRule.value === 'set'
      ? { field: 'author', kind: 'set', value: authorValue.value }
      : { field: 'author', kind: 'replace', find: find.value, replacement: replacement.value }
  }
  return titleRule.value === 'derive'
    ? { field: 'title', kind: 'derive' }
    : { field: 'title', kind: 'replace', find: find.value, replacement: replacement.value }
})

// 預覽與寫入呼叫同一個函式，因此畫面上看到的就是會寫進去的。
/**
 * 直接匯入的作品相對路徑是空的，重新推導要落回它那個來源的名稱（就是資料夾名），
 * 與加入作品時同一條規則。
 */
const editable = computed(() => store.selectedComics.map(comic => ({
  ...comic, sourceName: local.libraries.find(library => library.id === comic.libraryId)?.name ?? '',
})))
const results = computed(() => previewEdit(editable.value, rule.value))
const summary = computed(() => summarise(results.value))
const invalidTitles = computed(() => results.value.filter(result => result.state === 'invalid').map(result => result.currentValue))
const changed = computed(() => results.value.filter(result => result.state === 'changed'))
// 改名只作用在單本作品；選取裡的集合在這裡就講清楚，不必等套用完才知道。
const collectionNotice = computed(() => store.skippedCollectionCount ? `（另有 ${store.skippedCollectionCount} 個集合不在範圍內）` : '')
const blocked = computed(() => busy.value || !!summary.value.invalid || !changed.value.length)
const fieldLabel = computed(() => field.value === 'title' ? '標題' : '作者')
const untouched = computed(() => field.value === 'title'
  ? '只改標題，作者、分類、封面與閱讀進度都不動。'
  : '只改作者，標題、分類、封面與閱讀進度都不動。')

const stateLabels: Record<string, string> = { changed: '會變更', unchanged: '不變', invalid: '無效' }

async function apply() {
  if (blocked.value) return
  busy.value = true
  error.value = ''
  try {
    const byId = new Map(store.selectedComics.map(comic => [comic.id, comic]))
    const outcome: BulkOutcome = await editComics(field.value, changed.value.flatMap(result => {
      const comic = byId.get(result.id)
      return comic ? [{ comic, value: result.resultingValue }] : []
    }))
    await cloud.refresh(true)
    emit('done', outcome, field.value)
    if (!outcome.reason) emit('close')
    else error.value = outcome.reason
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : `${fieldLabel.value}尚未更新`
  } finally { busy.value = false }
}
</script>

<template>
  <LibraryOverlay
    :open="open"
    title="批次編輯"
    :description="`套用到已選取的 ${results.length} 本作品${collectionNotice}。${untouched}`"
    :pending="busy"
    size="2xl"
    @close="emit('close')"
  >
    <InlineAlert v-if="error" tone="error" class="mb-3">{{ error }}</InlineAlert>

    <Field>
      <Label>要編輯哪個欄位</Label>
      <RadioGroup v-model="field">
        <RadioField><Radio value="title" color="primary" /><Label>標題</Label></RadioField>
        <RadioField><Radio value="author" color="primary" /><Label>作者</Label></RadioField>
      </RadioGroup>
    </Field>

    <Field class="mt-5">
      <Label>規則</Label>
      <RadioGroup v-if="field === 'title'" v-model="titleRule">
        <RadioField v-if="canDerive">
          <Radio value="derive" color="primary" />
          <Label>依資料夾路徑重新推導標題</Label>
          <Description>用目前的推導規則重算，可一次修掉重複的作品名前綴。</Description>
        </RadioField>
        <RadioField>
          <Radio value="replace" color="primary" />
          <Label>尋找與取代</Label>
          <Description>純文字比對、區分大小寫、取代全部出現處，不接受正規表示式。</Description>
        </RadioField>
      </RadioGroup>
      <!-- 作者不提供依路徑推導：資料夾路徑正是作者通常不存在的地方。 -->
      <RadioGroup v-else v-model="authorRule">
        <RadioField>
          <Radio value="set" color="primary" />
          <Label>設為</Label>
          <Description>選取的作品都改成同一個作者，一部作品的各卷通常就是這種情況。</Description>
        </RadioField>
        <RadioField>
          <Radio value="replace" color="primary" />
          <Label>尋找與取代</Label>
          <Description>純文字比對、區分大小寫、取代全部出現處，不接受正規表示式。</Description>
        </RadioField>
      </RadioGroup>
    </Field>

    <Field v-if="field === 'author' && authorRule === 'set'" class="mt-3">
      <Label>作者</Label>
      <Input v-model="authorValue" autocomplete="off" placeholder="留空表示清除作者" />
    </Field>
    <div v-else-if="usingReplace" class="rename-replace-row">
      <Field><Label>尋找</Label><Input v-model="find" autocomplete="off" placeholder="例如：獵人 獵人" /></Field>
      <Field><Label>取代為</Label><Input v-model="replacement" autocomplete="off" placeholder="留空表示刪除" /></Field>
    </div>

    <InlineAlert v-if="summary.invalid" tone="error" class="mt-3">
      有 {{ summary.invalid }} 本的標題會變成空白，因此無法套用：{{ invalidTitles.join('、') }}
    </InlineAlert>
    <ErrorMessage v-else-if="usingReplace && !find" class="mt-3">請填寫要尋找的文字。</ErrorMessage>

    <p class="rename-summary">會變更 {{ summary.changed }} 本 · 不變 {{ summary.unchanged }} 本 · 無效 {{ summary.invalid }} 本</p>
    <!-- 逐本列出，不抽樣不截斷：批次編輯沒有復原機制，預覽是唯一的防線。 -->
    <ul class="rename-preview">
      <li v-for="result in results" :key="result.id" :class="`is-${result.state}`">
        <span class="rename-from">{{ result.currentValue || '（空白）' }}</span>
        <span aria-hidden="true">→</span>
        <span class="rename-to">{{ result.resultingValue || '（空白）' }}</span>
        <small>{{ stateLabels[result.state] }}</small>
      </li>
    </ul>

    <template #actions>
      <Button outline :disabled="busy" @click="emit('close')">取消</Button>
      <Button color="primary" :disabled="blocked" @click="apply">
        {{ busy ? '正在套用…' : `套用（${summary.changed} 本）` }}
      </Button>
    </template>
  </LibraryOverlay>
</template>
